import {
  Avatar,
  dateFormatterDayMonthLong,
  dateFormatterMonthLong,
  HomeChargingSupportingDocumentStatus,
  LaunchPage,
  MaterialUiColumnsProps,
  MaterialUiTable,
  MessageTooltip,
  MonthPeriod,
  SelectDS,
  Spacer,
  TableTagHomeChargingSupportingDocumentStatus,
  usePersistMuiTableState,
  WarningOutlined,
} from "@qivia/ui";
import { Option } from "@qivia/ui/src/designSystem/components/Select";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  addMonths,
  endOfMonth,
  isSameMonth,
  isThisMonth,
  startOfMonth,
  subMonths,
} from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  formatCurrency,
  formatDateWithoutDay,
} from "../../../libSlice/materialUiFormats";
import {
  getHomeChargingSupportingDocumentsAsync,
  getHomeChargingSupportingDocumentsMonthsAsync,
  homeChargingSupportingDocumentsSlice,
  selectHomeChargingSupportingDocuments,
  selectHomeChargingSupportingDocumentsMonths,
  selectHomeChargingSupportingDocumentsMonthsStatus,
  selectHomeChargingSupportingDocumentsStatus,
} from "./homeChargingSupportingDocumentsSlice";
import {
  HomeChargingSupportingDocumentDisplayed,
  HomeChargingSupportingDocumentRow,
} from "./homeChargingSupportingDocumentsAPI";
import { SupportingDocumentsSummary } from "./supportingDocumentsSummary";
import { getHomeChargingSupportingDocumentStatus } from "./lib";
import { SupportingDocumentSidePanel } from "./sidePanel/supportingDocumentSidePanel/SidePanel";
import "../../../i18n/i18n";

export const SupportingDocumentsTab = () => {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const firstPath = location.pathname.split("/")[1];
  const dispatch = useAppDispatch();
  const supportingDocuments = useAppSelector(
    selectHomeChargingSupportingDocuments,
  );
  const supportingDocumentsStatus = useAppSelector(
    selectHomeChargingSupportingDocumentsStatus,
  );
  const months = useAppSelector(selectHomeChargingSupportingDocumentsMonths);
  const monthsStatus = useAppSelector(
    selectHomeChargingSupportingDocumentsMonthsStatus,
  );
  const [selectedMonthPeriod, setSelectedMonthPeriod] = useState<MonthPeriod>([
    startOfMonth(subMonths(new Date(), 1)).toISOString(),
    endOfMonth(subMonths(new Date(), 1)).toISOString(),
  ]);
  const [listDisplayed, setListDisplayed] = useState<
    HomeChargingSupportingDocumentDisplayed[] | null
  >(null);

  const pageName = "home_charging_supporting_documents";
  const apiRef = useGridApiRef();
  const dataGridState = usePersistMuiTableState(
    apiRef,
    "customers-grid",
    pageName,
  );

  useEffect(() => {
    if (monthsStatus === "idle") {
      void dispatch(getHomeChargingSupportingDocumentsMonthsAsync());
    }
  }, [dispatch, monthsStatus]);

  useEffect(() => {
    if (supportingDocumentsStatus === "idle") {
      const period = {
        from: selectedMonthPeriod[0],
        to: selectedMonthPeriod[1],
      };
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.setHomeChargingSupportingDocumentsPeriod(
          period,
        ),
      );
      void dispatch(getHomeChargingSupportingDocumentsAsync(period));
    }
  }, [dispatch, selectedMonthPeriod, supportingDocumentsStatus]);

  const periodOptions = useMemo(
    (): Option<string>[] =>
      [...months]
        .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
        .map((month) => ({
          value: month,
          label: formatDateWithoutDay(month),
        })),
    [months],
  );

  const selectedPeriodOption = useMemo(
    () =>
      periodOptions.find((option) =>
        isSameMonth(new Date(option.value), new Date(selectedMonthPeriod[0])),
      ) ?? null,
    [periodOptions, selectedMonthPeriod],
  );

  const onChangePeriod = useCallback(
    (option: Option<string> | null) => {
      if (!option) return;
      const monthPeriod: MonthPeriod = [
        startOfMonth(new Date(option.value)).toISOString(),
        endOfMonth(new Date(option.value)).toISOString(),
      ];
      const period = {
        from: monthPeriod[0],
        to: monthPeriod[1],
      };
      setSelectedMonthPeriod(monthPeriod);
      dispatch(
        homeChargingSupportingDocumentsSlice.actions.setHomeChargingSupportingDocumentsPeriod(
          period,
        ),
      );
      void dispatch(getHomeChargingSupportingDocumentsAsync(period));
    },
    [dispatch],
  );

  const isCurrentMonth = useMemo(
    () => isThisMonth(new Date(selectedMonthPeriod[0])),
    [selectedMonthPeriod],
  );

  const allSupportingDocumentsDisplayed: HomeChargingSupportingDocumentRow[] =
    useMemo(() => {
      return supportingDocuments.map((supportingDocument) => {
        const row: HomeChargingSupportingDocumentRow = {
          firstName: supportingDocument.firstName,
          lastName: supportingDocument.lastName,
          chargesCount: supportingDocument.chargesCount,
          totalEnergy: supportingDocument.totalEnergy,
          reimbursementAmount: supportingDocument.reimbursementAmount,
          status: getHomeChargingSupportingDocumentStatus(
            supportingDocument,
            isCurrentMonth,
          ),
          createdAt: supportingDocument.supportingDocument?.createdAt ?? null,
          createdBy: supportingDocument.supportingDocument?.createdBy ?? null,
          id: supportingDocument.driverUuid,
        };
        return row;
      });
    }, [supportingDocuments, isCurrentMonth]);

  useEffect(() => {
    if (supportingDocumentsStatus === "success") {
      setListDisplayed(allSupportingDocumentsDisplayed);
    }
  }, [allSupportingDocumentsDisplayed, supportingDocumentsStatus]);

  const searchBarProps = {
    values: allSupportingDocumentsDisplayed,
    setFilterValues: setListDisplayed,
    keysToTranslate: ["status" as const],
    findKeysToTranslate: (
      _key: keyof HomeChargingSupportingDocumentDisplayed,
      value: string,
    ) => t(`home_charging_supporting_documents.status.${value}`),
    keysToIgnore: ["id" as const, "createdAt" as const, "createdBy" as const],
  };

  const columnsDisplayed: {
    [index in keyof HomeChargingSupportingDocumentDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      firstName: {
        type: "string",
        width: 250,
        valueFormatter: (value: string, row) => {
          const lastName = row["lastName"] as string;
          return (
            <StyledRow>
              <Avatar
                color={"mint"}
                size={"S"}
                firstName={value}
                lastName={lastName}
              />
              <Spacer x={0.75} />
              {value} {lastName}
            </StyledRow>
          );
        },
      },
      chargesCount: {
        type: "number",
        width: 160,
      },
      totalEnergy: {
        type: "number",
        width: 160,
      },
      reimbursementAmount: {
        type: "number",
        width: 160,
        valueFormatter: (value: number) => (
          <StyledAmount>{formatCurrency(value)}</StyledAmount>
        ),
      },
      status: {
        type: "string",
        width: 250,
        renderCell: (p) => (
          <StyledRow>
            <TableTagHomeChargingSupportingDocumentStatus
              status={p.row.status as HomeChargingSupportingDocumentStatus}
              createdAt={
                p.row
                  .createdAt as HomeChargingSupportingDocumentRow["createdAt"]
              }
              createdBy={
                p.row
                  .createdBy as HomeChargingSupportingDocumentRow["createdBy"]
              }
            />
          </StyledRow>
        ),
      },
    };
  }, []);

  const openSidePanelAction = useCallback(
    (
      row: Partial<HomeChargingSupportingDocumentDisplayed> & { id?: string },
    ) => {
      navigate(
        `/${firstPath}/electric_charging/supporting_documents/${row.id}`,
      );
    },
    [navigate, firstPath],
  );

  if (params.tab !== "supporting_documents") {
    return null;
  }

  return (
    <StyledContainer>
      <StyledHeader>
        <SelectDS
          label={""}
          options={periodOptions}
          placeholder={t(
            "home_charging_supporting_documents.periodPlaceholder",
          )}
          onChange={onChangePeriod}
          value={selectedPeriodOption}
          width={16}
          withoutSpacerBottom
        />
        <Spacer x={1.5} />
        <StyledLabel>
          {t("home_charging_supporting_documents.period", {
            lastDay: dateFormatterDayMonthLong(
              new Date(selectedMonthPeriod[1]),
            ),
          })}
        </StyledLabel>
      </StyledHeader>
      <Spacer y={2} />
      {isCurrentMonth && (
        <>
          <MessageTooltip
            type="WARNING"
            iconLeft={<WarningOutlined />}
            title={t("home_charging_supporting_documents.currentMonth.title", {
              nextMonth: dateFormatterMonthLong(
                addMonths(new Date(selectedMonthPeriod[0]), 1),
              ),
            })}
            text={t("home_charging_supporting_documents.currentMonth.text", {
              month: dateFormatterMonthLong(new Date(selectedMonthPeriod[0])),
            })}
          />
          <Spacer y={2} />
        </>
      )}
      <SupportingDocumentsSummary />
      <Spacer y={2} />
      {!listDisplayed ? (
        <LaunchPage hasBorderRadius={true} />
      ) : (
        <MaterialUiTable<
          keyof HomeChargingSupportingDocumentDisplayed,
          HomeChargingSupportingDocumentDisplayed
        >
          rows={listDisplayed}
          columns={columnsDisplayed}
          pageName={pageName}
          dataGridState={dataGridState}
          searchBar={searchBarProps}
          apiRef={apiRef}
          onRowClickAction={openSidePanelAction}
        />
      )}
      <SupportingDocumentSidePanel />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRow = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const StyledLabel = styled.div`
  ${typographies["Body/M"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledAmount = styled.div`
  ${typographies["Body/L"]}
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;
